import { useQuery } from "@tanstack/react-query";

import { apiClient } from "@/api/apiClient";
import {
  getProviderRequestApiV1ProvidersMeDocumentRequestsRequestIdGet,
  listProviderRequestsApiV1ProvidersMeDocumentRequestsGet,
} from "@/api/generated/sdk.gen";
import type {
  ProviderPortalRequestDetailSchema,
  ProviderPortalRequestListItemSchema,
} from "@/api/generated/types.gen";

import { CLIENT_KEYS } from "@constants/clientKeys";

/**
 * Provider portal (MDRS-157): the document requests referrers raised to the
 * logged-in provider, scoped server-side to the provider's identity. Drives the
 * Requests list.
 */
export const useProviderRequests = ({
  enabled = true,
}: { enabled?: boolean } = {}) => {
  return useQuery({
    queryKey: [CLIENT_KEYS.PROVIDER_REQUESTS],
    queryFn: async (): Promise<ProviderPortalRequestListItemSchema[]> => {
      const response = await listProviderRequestsApiV1ProvidersMeDocumentRequestsGet(
        {
          client: apiClient,
        }
      );
      if ("error" in response && response.error) {
        throw new Error(
          `Failed to fetch provider requests: ${
            (response.error as { detail?: string })?.detail ||
            JSON.stringify(response.error)
          }`
        );
      }
      return response.data ?? [];
    },
    enabled,
  });
};

/** One request's detail (checklist + case fields), scoped to the provider. */
export const useProviderRequest = (
  requestId: string | undefined,
  { enabled = true }: { enabled?: boolean } = {}
) => {
  return useQuery({
    queryKey: [CLIENT_KEYS.PROVIDER_REQUESTS, requestId],
    queryFn: async (): Promise<ProviderPortalRequestDetailSchema> => {
      const response =
        await getProviderRequestApiV1ProvidersMeDocumentRequestsRequestIdGet({
          client: apiClient,
          path: { request_id: requestId as string },
        });
      if ("error" in response && response.error) {
        throw new Error(
          `Failed to fetch request: ${
            (response.error as { detail?: string })?.detail ||
            JSON.stringify(response.error)
          }`
        );
      }
      if (!response.data) {
        throw new Error("Request not found");
      }
      return response.data;
    },
    enabled: enabled && Boolean(requestId),
  });
};
